import {
	ChangeDetectionStrategy,
	Component,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { convertLLCAddressToCloserAddress } from '@core/util/address';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import {
	CloserSyncTransaction,
	CloserSyncInstruction,
} from '@models/mortgages/closer-sync-instruction';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { CloserSyncInstructionRepoService } from '@repos/mortgages/repos/closerSyncInstructionRepo';
import { CloserSyncTransactionRepoService } from '@repos/mortgages/repos/closerSyncTransactionRepo';
import {
	MatterMortgageDispatch,
	MatterMortgagesState,
} from '@state/matter/matter_mortgages';
import { BehaviorSubject, Observable } from 'rxjs';

const closerSyncIdsToShowContactInfo = ['2', '5']; // BMO & Libro

@UntilDestroy()
@Component({
	selector: 'app-closer-sync-lenders',
	templateUrl: './closer-sync-lenders.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CloserSyncLendersComponent implements OnDestroy, OnInit {
	@Select(MatterMortgagesState.getSelectedMortgage)
	mortgage$: Observable<MatterMortgageWithAssociations>;

	public _mortgage: MatterMortgageWithAssociations;
	private transactionSubject = new BehaviorSubject<CloserSyncTransaction>(null);
	private instructionSubject = new BehaviorSubject<CloserSyncInstruction>(null);
	public instruction$: Observable<CloserSyncInstruction> =
		this.instructionSubject.asObservable();
	mortgageeServiceAddress: any;
	mortgageeReportAddress: any;
	closerSyncIdsToShowContactInfo = closerSyncIdsToShowContactInfo;
	showContactInfo = false;
	showMortgageFunderDetails = false;
	currentLenderSyncId = '';

	constructor(
		private mortgageDispatch: MatterMortgageDispatch,
		private transactionRepo: CloserSyncTransactionRepoService,
		private instructionsRepo: CloserSyncInstructionRepoService,
	) {}

	ngOnDestroy(): void {
		this.instructionSubject.unsubscribe();
		this.transactionSubject.unsubscribe();
	}

	async ngOnInit(): Promise<void> {
		this.mortgage$.pipe(untilDestroyed(this)).subscribe(async (mortgage) => {
			this._mortgage = mortgage;
			this.currentLenderSyncId = Object.values(
				mortgage?.lenders,
			)?.[0]?.closerSyncId;

			const transactionId = mortgage?.relatedMortgageId;
			const transactionDoc = await this.transactionRepo
				.Get(transactionId)
				.toPromise();
			const latestInstructionId = transactionDoc.data()?.latestVersionId;
			const instructionDoc = await this.instructionsRepo
				.Get(latestInstructionId, { transactionId })
				.toPromise();

			this.transactionSubject.next(
				transactionDoc.data() as CloserSyncTransaction,
			);
			this.instructionSubject.next(
				instructionDoc.data() as CloserSyncInstruction,
			);

			if (this.instructionSubject?.value?.mortgagee) {
				this.mortgageeServiceAddress = convertLLCAddressToCloserAddress(
					this.instructionSubject.value.mortgagee.serviceAddress?.address,
				);
				this.mortgageeReportAddress = convertLLCAddressToCloserAddress(
					this.instructionSubject.value.mortgagee.reportAddress?.address,
				);
			}

			// TODO the following should be refactored to not be hardcoded. Perhaps a config setting in Adminzer?
			this.showContactInfo =
				this.currentLenderSyncId === '5' ||
				(this.currentLenderSyncId === '2' &&
					mortgage?.chargeType !== 'Conventional (BrokerEdge)');
		});
	}

	setActingForMortgagee(value: boolean) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			actingForMortgagee: value,
		});
	}

	setLenderContactName(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			lenderContactName: value,
		});
	}

	setLenderContactPhoneNumber(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			lenderContactPhoneNumber: value,
		});
	}
}
